import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Loader from "@/components/shared/Loader";
import { useToast } from "@/components/ui/use-toast";

import { SignInValidation } from "@/lib/validation";
import { useSignInAccount } from "@/lib/react-query/queries";
import { useUserContext } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { initiateGoogleAuth } from "@/lib/appwrite/api";
import { account } from "@/lib/appwrite/config";
import { Eye, EyeOff } from "lucide-react";

const SigninForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { checkAuthUser } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const isBanned = searchParams.get('banned') === 'true';
  const [userIsBanned, setUserIsBanned] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof SignInValidation>>({
    resolver: zodResolver(SignInValidation),
    defaultValues: {
      emailOrPhone: "",
      password: "",
    },
  });

  const { mutateAsync: signInAccount } = useSignInAccount();

  // Show banned message when component mounts if banned=true
  useEffect(() => {
    if (isBanned) {
    setUserIsBanned(true);
      toast({
        title: "Account Suspended",
        description: "Your account is under ban due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
        variant: "destructive"
      });
    }
  }, [isBanned, toast]);


const handleSignIn = async (values: z.infer<typeof SignInValidation>) => {
  setIsLoading(true);
  try {
    let session;
    if(!values.emailOrPhone.includes('@')){
      const phoneNumber = `+91${values.emailOrPhone}`;
      session = await signInAccount({
        emailOrPhone: phoneNumber,
        password: values.password,
      });
    }else{
      session = await signInAccount({
        emailOrPhone: values.emailOrPhone,
        password: values.password,
      });
    }
    
    if (!session) {
      // Don't show toast if user is banned (they'll see the violation UI)
      if (!userIsBanned) {
        toast({ 
          title: "Sign in failed",
          description: "Please check your credentials and try again.",
          variant: "destructive"
        });
      }
      return;
    }

    const isAuthenticated = await checkAuthUser();
    
    if (!isAuthenticated) {
      // Don't show toast if user is banned (they'll see the violation UI)
      if (!userIsBanned) {
        toast({ 
          title: "Sign in failed",
          description: "Please check your credentials and try again.",
          variant: "destructive"
        });
      }
      return;
    }

    // If authentication is successful
    toast({ 
      title: "Sign in successful",
      description: "Welcome back!"
    });

    navigate("/");
  } catch (error) {
    console.error("SignIn error:", error);
    
    // Check if the error indicates user is banned and update state
    if (error instanceof Error && error.message.includes('ban')) {
      setUserIsBanned(true);
    }
    
    // Don't show toast if user is banned (they'll see the violation UI)
    if (!userIsBanned) {
      toast({ 
        title: "Sign in failed", 
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive"
      });
    }
  } finally {
    setIsLoading(false);
  }
};

  const handleGoogleLogin = async (e: any) => {
    e.preventDefault();
    try {
      await initiateGoogleAuth(account);
      // The page will redirect to Google login,
      // so no code here will execute immediately
    } catch (error) {
      // Optional: Show a user-friendly error toast or modal
      toast({ 
        title: "Google Login Failed", 
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive",
      });
    }
  };

  // If user is banned, show banned message instead of login form
  if (isBanned) {
    return (
      <div className="sm:w-420 flex-center flex-col p-6">
        <img src="/assets/images/busiconn.svg" alt="busiconn" />
        
        <div className="flex flex-col items-center text-center mt-8">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-rose-800 mb-3">
              Account Suspended
            </h2>
            <p className="text-rose-700 mb-4">
              Your account is under ban due to repeated violations of our community guidelines.
            </p>
            <p className="text-rose-600 text-sm">
              For assistance, please contact:{" "}
              <a 
                href="mailto:contact@busiconn.com" 
                className="text-rose-800 underline hover:text-rose-900"
              >
                contact@busiconn.com
              </a>
            </p>
          </div>
          
          <Button 
            onClick={() => navigate('/sign-in')}
            variant="outline"
            className="mt-4 text-gray-700"
          >
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <div className="sm:w-420 flex-center flex-col p-6">
        <img src="/assets/images/busiconn.svg" alt="busiconn" />

        <h2 className="h3-bold md:h2-bold pt-5 sm:pt-12">
          Log in to your account
        </h2>
        <p className="text-light-3 small-medium md:base-regular mt-2">
          Welcome back! Please enter your details
        </p>

        {/* NEW: User Guide Link - Only addition to your existing code */}
        {/* <div className="w-full mt-3 mb-1">
          <Link
            to="/busiconn-user-guide"
            className="flex items-center justify-center w-full py-2 px-4 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            New to Busiconn? Read our User Guide
          </Link>
        </div> */}

        <form 
          onSubmit={form.handleSubmit(handleSignIn)}
          className="flex flex-col gap-5 w-full mt-4">
          <FormField
            control={form.control}
            name="emailOrPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Phone</FormLabel>
                <FormControl>
                  <Input 
                    type="text" 
                    placeholder="Your 10 digit mobile number"
                    className="shad-input" 
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input 
                      type={showPassword ? "text" : "password"}
                      className="shad-input pr-10"
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="shad-button_primary"
            disabled={isLoading}
          >
            {isLoading ? 
            ( <div className="flex-center gap-2">
              <Loader /> 
            </div>
          ) : "Log in"}
          </Button>

          <p className="text-small-regular text-light-2 text-center mt-2">
            Don&apos;t have an account?
            <Link
              to="/sign-up"
              className="text-primary-500 text-small-semibold ml-1">
              Sign up
            </Link>
          </p>
        </form>
        <Button 
      variant="secondary" 
      className="shad-button_ghost" 
      onClick={(e) => handleGoogleLogin(e)} 
      style={{ marginLeft: '10px', marginTop:'10px' }}
    >
     Sign in with Google
    </Button>
      </div>
    </Form>
  );
};

export default SigninForm;